import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppSettingsService } from '../config/config-service';
import { AppConfig } from '../config/app-config';
import { CommonService } from './common.service';

export interface WithdrawalRequestData {
  amount: number;
  userNotes?: string;
}

export interface ProcessRequestData {
    action: 'approve' | 'reject';
    adminNotes?: string;
    transactionReference?: string;
}export interface WithdrawalItem {
  _id: string;
  userId: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  } | string;
  amount: number;
  feeAmount: number;
  netAmount: number;
  status: 'Requested' | 'Initiated' | 'Processing' | 'Completed' | 'Rejected' | 'Canceled';
  payoutMethod?: string | null;
  userNotes?: string;
  referenceNumber?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Paginated<T> {
  success: boolean;
  data: T[];
  pagination: { page: number; limit: number; total: number; pages: number; };
}

@Injectable({
  providedIn: 'root'
})
export class WithdrawalService {
  private apiUrl: string;

  constructor(private http: CommonService, private appSettingsService: AppSettingsService) {
    this.apiUrl =  this.appSettingsService.getSettings().apiEndPoint || ''
   }

  /**
   * User creates a new withdrawal request.
   * @param data The withdrawal request payload.
   */
  createRequest(data: WithdrawalRequestData): Observable<any> {
    const url = `${this.apiUrl}${AppConfig.requestWithdrawal}`;
    return this.http.post(url, data);
  }

  /**
   * Gets withdrawal history for the current user.
   * @param page Page number
   * @param limit Items per page
   */
  getWithdrawalsHistory(page: number = 1, limit: number = 20): Observable<any> {
    const url = `${this.apiUrl}${AppConfig.getWithdrawalsHistory}`;
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    return this.http.get(url, { params });
  }

  /**
   * Gets a list of withdrawal requests based on filters.
   * @param filters An object containing filter criteria like userId or status.
   */
  getRequests(filters: { page?: number; limit?: number; status?: string; userId?: string; } = {}): Observable<any> {
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params = params.append(key, value.toString());
      }
    });
    return this.http.get(`${this.apiUrl}/api/withdrawals/list`, { params });
  }

  /**
   * Admin processes a withdrawal request.
   * @param requestId The ID of the request to process.
   * @param data The payload containing the action and notes.
   */
  processRequest(requestId: string, data: ProcessRequestData): Observable<any> {
    return this.http.patch(`${this.apiUrl}/api/withdrawals/process/${requestId}`, data);
  }

  /**
   * Gets the current user's wallet balance.
   */
  getWalletBalance(): Observable<any> {
      return this.http.get(`${this.apiUrl}/api/wallets/balance`);
  }
  
    getAll(params: { status?: string; page?: number; limit?: number; userId?: string; startDate?: string; endDate?: string; search?: string }): Observable<Paginated<WithdrawalItem>> {
      let p = new HttpParams();
      if (params?.status) p = p.set('status', params.status);
      if (params?.page) p = p.set('page', String(params.page ?? 1));
      if (params?.limit) p = p.set('limit', String(params.limit ?? 20));
      if (params?.userId) p = p.set('userId', params.userId);
      if (params?.startDate) p = p.set('startDate', params.startDate);
      if (params?.endDate) p = p.set('endDate', params.endDate);
      if (params?.search) p = p.set('search', params.search);
      const url = `${this.apiUrl}/api/withdrawals/getAllWithdrawals`;
      return this.http.get<Paginated<WithdrawalItem>>(url, { params: p });
    }
  
    actOnWithdrawal(id: string, body: { action: 'initiate'|'process'|'complete'|'reject'; referenceNumber?: string; adminNotes?: string }) {
      const url = `${this.apiUrl}/api/withdrawals/${id}/adminActOnWithdrawal`;
      return this.http.patch<{ success: boolean; data: WithdrawalItem }>(url, body);
    }
}